import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { ArrowRight, ArrowLeft, Car, Sparkles, Mail, Phone, CheckCircle2, ChevronDown, Clock } from 'lucide-react';
import { calculateQuote } from '../mock';
import axios from 'axios';
import { toast } from 'sonner';
import zipcodes from 'zipcodes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_ZIP = '32792'; // Winter Park, FL

const QuoteWizard = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    garageSize: '',
    heavyItems: false,
    sweeping: false,
    pressureWashing: false,
    name: '',
    email: '',
    phone: '',
    zipCode: '',
    appointmentDate: '',
    appointmentTime: ''
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);
  const [existingAppointments, setExistingAppointments] = useState([]);

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  // Fetch existing appointments when scheduling modal opens
  useEffect(() => {
    if (showScheduling) {
      fetchExistingAppointments();
    }
  }, [showScheduling]);

  // Calculate distance-based fee
  const calculateDistanceFee = (zipCode) => {
    try {
      const baseLocation = zipcodes.lookup(BASE_ZIP);
      const customerLocation = zipcodes.lookup(zipCode);
      
      if (!baseLocation || !customerLocation) {
        return { distance: 0, fee: 0, feePercentage: 0 };
      }
      
      const distance = zipcodes.distance(BASE_ZIP, zipCode);
      
      // No fee within 10 miles
      if (distance <= 10) {
        return { distance, fee: 0, feePercentage: 0 };
      }
      
      // Calculate miles outside of 10 mile radius
      const milesOutside = distance - 10;
      
      // 5% for every 5 miles outside 10 mile radius
      const feeMultiplier = Math.ceil(milesOutside / 5);
      const feePercentage = feeMultiplier * 5;
      
      return { distance, fee: 0, feePercentage }; // fee will be calculated based on total
    } catch (error) {
      console.error('Error calculating distance:', error);
      return { distance: 0, fee: 0, feePercentage: 0 };
    }
  };

  const handleNext = async () => {
    if (step === 3) {
      setLoading(true);
      try {
        // Calculate quote
        const quoteResult = calculateQuote(formData.garageSize, {
          heavyItems: formData.heavyItems,
          sweeping: formData.sweeping,
          pressureWashing: formData.pressureWashing
        });
        
        // Calculate distance fee
        const distanceInfo = calculateDistanceFee(formData.zipCode);
        const subtotal = quoteResult.totalPrice;
        const distanceFee = (subtotal * distanceInfo.feePercentage) / 100;
        const finalTotal = subtotal + distanceFee;
        
        const calculatedQuote = {
          garageSize: formData.garageSize,
          heavyItems: formData.heavyItems,
          sweeping: formData.sweeping,
          pressureWashing: formData.pressureWashing,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          zipCode: formData.zipCode,
          basePrice: quoteResult.basePrice,
          additionalCost: quoteResult.additionalCost,
          distance: distanceInfo.distance,
          distanceFee: distanceFee,
          distanceFeePercentage: distanceInfo.feePercentage,
          totalPrice: finalTotal
        };
        
        // Save quote immediately to database
        const response = await axios.post(`${API}/quotes`, calculatedQuote);
        setQuote(response.data);
        toast.success('Quote generated successfully!');
      } catch (error) {
        console.error('Error saving quote:', error);
        toast.error('Failed to generate quote. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setStep(step + 1);
    }
  };
  
  const fetchExistingAppointments = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      const appointments = response.data.filter(q => q.appointmentDate && q.appointmentTime);
      setExistingAppointments(appointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const handleScheduleAppointment = async () => {
    if (!formData.appointmentDate || !formData.appointmentTime) {
      toast.error('Please select a date and time');
      return;
    }
    
    setLoading(true);
    try {
      // Update existing quote with appointment info
      const updatedQuote = {
        ...quote,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime
      };
      
      // Update the quote in backend
      await axios.put(`${API}/quotes/${quote.id}`, updatedQuote);
      setQuote(updatedQuote);
      setShowScheduling(false);
      toast.success('Appointment scheduled successfully!');
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      toast.error('Failed to schedule appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.garageSize !== '';
      case 2:
        return true; // Services are optional
      case 3:
        return formData.name && formData.email && formData.phone && formData.zipCode && formData.zipCode.length === 5;
      default:
        return false;
    }
  };

  if (quote && !showScheduling) {
    // Calculate estimated duration
    const getEstimatedDuration = () => {
      const baseHours = { '1-car': 2, '2-car': 3, '3-car': 4 };
      let hours = baseHours[quote.garageSize] || 2;
      if (quote.pressureWashing) hours += 1;
      if (quote.heavyItems) hours += 0.5;
      return `${Math.floor(hours)}-${Math.ceil(hours + 0.5)} hours`;
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <Card className="w-full max-w-2xl bg-white my-8">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="bg-emerald-100 p-4 rounded-full">
                <CheckCircle2 className="h-12 w-12 text-emerald-600" />
              </div>
            </div>
            <CardTitle className="text-3xl text-slate-900">Your Instant Quote</CardTitle>
            <CardDescription className="text-slate-600">Here's your customized garage cleaning estimate</CardDescription>
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-slate-600">
              <Clock className="h-4 w-4" />
              <span>Estimated completion: {getEstimatedDuration()}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-slate-50 p-6 rounded-lg space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Garage Size:</span>
                <span className="font-semibold text-slate-900 capitalize">{quote.garageSize}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Base Price:</span>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-2 mb-1">
                    <span className="text-slate-400 line-through text-sm">
                      ${(() => {
                        const originalPrices = { 209: 250, 379: 450, 629: 750 };
                        return originalPrices[quote.basePrice] || Math.round(quote.basePrice / 0.85);
                      })()}
                    </span>
                    <span className="inline-flex items-center bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                      🎄 Holiday Special
                    </span>
                  </div>
                  <span className="font-semibold text-emerald-600 text-lg">${quote.basePrice}</span>
                </div>
              </div>
              
              {quote.heavyItems && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">• Heavy Items Removal (25%):</span>
                  <span className="text-slate-700">+${(quote.basePrice * 0.25).toFixed(2)}</span>
                </div>
              )}
              
              {quote.sweeping && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">• Sweeping (5%):</span>
                  <span className="text-slate-700">+${(quote.basePrice * 0.05).toFixed(2)}</span>
                </div>
              )}
              
              {quote.pressureWashing && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">• Pressure Washing (50%):</span>
                  <span className="text-slate-700">+${(quote.basePrice * 0.50).toFixed(2)}</span>
                </div>
              )}
              
              {quote.distanceFee > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center text-sm mb-1">
                    <span className="text-slate-600">Distance from Winter Park:</span>
                    <span className="text-slate-700">{quote.distance.toFixed(1)} miles</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">• Travel Fee ({quote.distanceFeePercentage}%):</span>
                    <span className="text-slate-700">+${quote.distanceFee.toFixed(2)}</span>
                  </div>
                </div>
              )}
              
              <div className="border-t border-slate-300 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-slate-900">Total Price:</span>
                  <span className="text-3xl font-bold text-emerald-600">${quote.totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* What's Included Expandable */}
            <Collapsible className="border border-slate-200 rounded-lg">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <span className="font-semibold text-slate-900">What's Included?</span>
                <ChevronDown className="h-5 w-5 text-slate-500" />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="space-y-3 text-sm text-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 mb-1">Base Service:</p>
                    <ul className="space-y-1 ml-4">
                      <li>• Complete decluttering and organization</li>
                      <li>• Sorting into labeled, organized bins</li>
                      <li>• Removal of trash and unwanted items</li>
                      <li>• Donation center drop-off for eligible items</li>
                    </ul>
                  </div>
                  {quote.heavyItems && (
                    <div>
                      <p className="font-medium text-slate-900 mb-1">Heavy Items Removal:</p>
                      <p className="ml-4">• Move or haul away large, bulky items from your garage</p>
                    </div>
                  )}
                  {quote.sweeping && (
                    <div>
                      <p className="font-medium text-slate-900 mb-1">Sweeping:</p>
                      <p className="ml-4">• Thorough floor sweeping and debris removal</p>
                    </div>
                  )}
                  {quote.pressureWashing && (
                    <div>
                      <p className="font-medium text-slate-900 mb-1">Pressure Washing:</p>
                      <p className="ml-4">• Deep clean floors and walls with professional pressure washing equipment</p>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {quote.id ? (
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-800">
                  <strong>Appointment Confirmed!</strong> Your garage cleaning is scheduled for <strong>{new Date(quote.appointmentDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong> at <strong>{quote.appointmentTime}</strong>. We'll contact you at <strong>{quote.email}</strong> or <strong>{quote.phone}</strong> to confirm.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  Ready to schedule your garage cleaning? Choose a convenient time!
                </p>
              </div>
            )}
            
            {quote.id ? (
              <Button 
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                onClick={onClose}
              >
                Done
              </Button>
            ) : (
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setQuote(null);
                    setStep(2);
                  }}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Edit Services
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setShowScheduling(true)}
                >
                  Schedule Appointment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Generate available dates (next 4 Fridays, Saturdays, Sundays)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    let daysChecked = 0;
    
    while (dates.length < 12 && daysChecked < 60) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + daysChecked);
      const dayOfWeek = checkDate.getDay();
      
      // 0 = Sunday, 5 = Friday, 6 = Saturday
      if (dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6) {
        dates.push(checkDate);
      }
      daysChecked++;
    }
    
    return dates;
  };
  
  const allTimes = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];
  
  // Convert time string to hours for comparison
  const timeToHours = (timeStr) => {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours + minutes / 60;
  };
  
  // Get available times for selected date
  const getAvailableTimes = () => {
    if (!formData.appointmentDate) return allTimes;
    
    // Find appointments on selected date
    const appointmentsOnDate = existingAppointments.filter(
      apt => apt.appointmentDate === formData.appointmentDate
    );
    
    if (appointmentsOnDate.length === 0) return allTimes;
    
    // Filter out times within 4 hours of existing appointments
    return allTimes.filter(time => {
      const currentTimeHours = timeToHours(time);
      
      return appointmentsOnDate.every(apt => {
        const bookedTimeHours = timeToHours(apt.appointmentTime);
        const timeDifference = Math.abs(currentTimeHours - bookedTimeHours);
        return timeDifference >= 4; // Must be at least 4 hours apart
      });
    });
  };
  
  const availableTimes = getAvailableTimes();

  if (showScheduling) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <Card className="w-full max-w-2xl bg-white my-8">
          <CardHeader>
            <div className="flex justify-between items-center mb-2">
              <CardTitle className="text-2xl text-slate-900">Schedule Your Appointment</CardTitle>
              <button 
                onClick={() => setShowScheduling(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <CardDescription>Choose a convenient date and time (Available Friday-Sunday)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-slate-700 mb-3 block text-lg font-semibold">Select Date</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {getAvailableDates().map((date) => (
                  <button
                    key={date.toISOString()}
                    onClick={() => setFormData({ ...formData, appointmentDate: date.toISOString().split('T')[0] })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.appointmentDate === date.toISOString().split('T')[0]
                        ? 'border-emerald-600 bg-emerald-50'
                        : 'border-slate-200 hover:border-emerald-200'
                    }`}
                  >
                    <div className="font-semibold text-slate-900">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-sm text-slate-600">
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-slate-700 mb-3 block text-lg font-semibold">Select Time</Label>
              {availableTimes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No available times on this date. Please select a different date.
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {availableTimes.map((time) => (
                    <button
                      key={time}
                      onClick={() => setFormData({ ...formData, appointmentTime: time })}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.appointmentTime === time
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-700 font-semibold'
                          : 'border-slate-200 hover:border-emerald-200 text-slate-700'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowScheduling(false)}
                className="flex-1"
              >
                Back to Quote
              </Button>
              <Button
                onClick={handleScheduleAppointment}
                disabled={!formData.appointmentDate || !formData.appointmentTime || loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {loading ? 'Scheduling...' : 'Confirm Appointment'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl bg-white my-8">
        <CardHeader>
          <div className="flex justify-between items-center mb-2">
            <CardTitle className="text-2xl text-slate-900">Get Your Quote</CardTitle>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              ✕
            </button>
          </div>
          <CardDescription>Step {step} of {totalSteps}</CardDescription>
          <Progress value={progress} className="mt-4" />
        </CardHeader>
        
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">What size is your garage?</h3>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 mb-6">
                  <p className="text-sm text-emerald-900 font-medium mb-2">Base Service Includes:</p>
                  <ul className="text-sm text-emerald-800 space-y-1">
                    <li>✓ Complete decluttering and organization</li>
                    <li>✓ Sorting belongings into labeled, organized bins</li>
                    <li>✓ Removal of trash and unwanted items</li>
                    <li>✓ Donation center drop-off for eligible items</li>
                  </ul>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['1-car', '2-car', '3-car'].map((size) => (
                    <button
                      key={size}
                      onClick={() => setFormData({ ...formData, garageSize: size })}
                      className={`p-6 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                        formData.garageSize === size
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-slate-200 bg-white hover:border-emerald-200'
                      }`}
                    >
                      <Car className={`h-8 w-8 mx-auto mb-3 ${
                        formData.garageSize === size ? 'text-emerald-600' : 'text-slate-400'
                      }`} />
                      <div className="font-semibold text-slate-900 capitalize">{size}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Additional Services</h3>
                <p className="text-slate-600 mb-6">Select any additional services you need</p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Checkbox 
                      id="heavyItems"
                      checked={formData.heavyItems}
                      onCheckedChange={(checked) => setFormData({ ...formData, heavyItems: checked })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="heavyItems" className="text-base font-medium text-slate-900 cursor-pointer">
                          Heavy Items Removal
                        </Label>
                        <span className="text-emerald-600 font-semibold">
                          {formData.garageSize && `+$${(() => {
                            const prices = { '1-car': 52.25, '2-car': 94.75, '3-car': 157.25 };
                            return prices[formData.garageSize].toFixed(2);
                          })()}`}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">Move or remove heavy items from your garage</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Checkbox 
                      id="sweeping"
                      checked={formData.sweeping}
                      onCheckedChange={(checked) => setFormData({ ...formData, sweeping: checked })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sweeping" className="text-base font-medium text-slate-900 cursor-pointer">
                          Sweeping
                        </Label>
                        <span className="text-emerald-600 font-semibold">
                          {formData.garageSize && `+$${(() => {
                            const prices = { '1-car': 10.45, '2-car': 18.95, '3-car': 31.45 };
                            return prices[formData.garageSize].toFixed(2);
                          })()}`}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">Thorough floor sweeping and debris removal</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Checkbox 
                      id="pressureWashing"
                      checked={formData.pressureWashing}
                      onCheckedChange={(checked) => setFormData({ ...formData, pressureWashing: checked })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="pressureWashing" className="text-base font-medium text-slate-900 cursor-pointer">
                          Pressure Washing
                        </Label>
                        <span className="text-emerald-600 font-semibold">
                          {formData.garageSize && `+$${(() => {
                            const prices = { '1-car': 104.50, '2-car': 189.50, '3-car': 314.50 };
                            return prices[formData.garageSize].toFixed(2);
                          })()}`}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">Deep clean floors and walls with pressure washing</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Your Information</h3>
                <p className="text-slate-600 mb-6">We'll send your quote and follow up to schedule service</p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-700 mb-2 block">
                      Email Address *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                      Phone Number *
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="zipCode" className="text-slate-700 mb-2 block">
                      Zip Code *
                    </Label>
                    <Input
                      id="zipCode"
                      type="text"
                      placeholder="32792"
                      maxLength={5}
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '') })}
                    />
                    <p className="text-xs text-slate-500 mt-1">Distance-based pricing applies outside 10 mile radius of Winter Park, FL (32792)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {loading ? 'Saving...' : (step === totalSteps ? 'Get Quote' : 'Next')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteWizard;